import { useCallback, useEffect, useRef } from "react";
import { REMOTE_RENDER_CONNECTION_ERROR_EVENT } from "../classes/contexts/RemoteRenderContext";
import { SnackbarType } from "../classes/enums/SnackbarTypeEnum";
import { useSnackbar } from "./useSnackbar";

/**
 * Hook that listens for remote render connection errors and displays notifications.
 * Should be used in a component that has access to both RemoteRenderContext and NotificationContext.
 */
export const useRemoteRenderNotifications = () => {
  const { showSnackbar } = useSnackbar();
  const activeNotificationRef = useRef(null);

  const handleConnectionError = useCallback(
    event => {
      const errorInfo = event.detail;
      if (!errorInfo) return;

      const { message, retriesExhausted, isRetrying, attempt, maxAttempts } =
        errorInfo;

      // Determine notification type and message
      let notificationType = SnackbarType.WARNING;
      let notificationMessage = message;
      let duration = 4000;

      if (retriesExhausted) {
        notificationType = SnackbarType.ERROR;
        notificationMessage =
          message ||
          "Unable to connect to rendering server. Please refresh the page to try again.";
        duration = 0; // Persistent notification for exhausted retries
      } else if (isRetrying) {
        notificationType = SnackbarType.WARNING;
        notificationMessage =
          message ||
          `Connection failed. Retrying (${attempt}/${maxAttempts})...`;
        duration = 3000;
      }

      // Show the notification
      activeNotificationRef.current = showSnackbar(
        notificationType,
        notificationMessage,
        {
          title: retriesExhausted ? "Connection Failed" : "Connection Issue",
          duration,
        }
      );
    },
    [showSnackbar]
  );

  useEffect(() => {
    window.addEventListener(
      REMOTE_RENDER_CONNECTION_ERROR_EVENT,
      handleConnectionError
    );

    return () => {
      window.removeEventListener(
        REMOTE_RENDER_CONNECTION_ERROR_EVENT,
        handleConnectionError
      );
    };
  }, [handleConnectionError]);
};
